/**
 * Monta um atleta completo em three.js (corpo, aparência, uniforme e acessório de posição), aplica poses de
 * `@besenliga/anim` e coloca o atleta na vassoura. Usado pelo cliente do jogo e pelas folhas de referência
 * em tools/render. Regras, cores e opções vêm de `@besenliga/shared`.
 */
import * as THREE from "three";
import { GLTFLoader, type GLTF } from "three/addons/loaders/GLTFLoader.js";
import { MeshoptDecoder } from "three/addons/libs/meshopt_decoder.module.js";
import {
  ACCESSORY_COLORS,
  FACE_SHAPES,
  HAIR_COLORS,
  HAIR_STYLES,
  HEIGHTS,
  HELMET_HIDES_HAIR,
  POSITION_ACCESSORY,
  SKIN_TONES,
  type Appearance,
  type Build,
  type Height,
  type Kit,
  type Position,
  type Sex,
} from "@besenliga/shared";
import type { AnimBone, Grip, Pose } from "@besenliga/anim";
import { PART, mergeAthlete, modelSkeleton, type ModelSkeleton, type Part } from "./merged.js";

export { PART, type Part } from "./merged.js";

/** Corpo + aparência + posição. As chaves seguem o vocabulário do GDD. */
export interface AthleteSpec extends Appearance {
  sexo: Sex;
  altura: Height;
  biotipo: Build;
  posicao?: Position;
}

export type AthleteModels = Record<Sex, GLTF>;

export interface AthleteRig {
  root: THREE.Object3D;
  bones: Record<string, THREE.Bone>;
  /** A malha única do atleta (corpo e peças). */
  mesh: THREE.SkinnedMesh;
  outline: THREE.SkinnedMesh | null;
  lod: number;
  setGeometryLod(level: number): void;
}

export interface BuildOptions {
  /** Espessura do contorno em pixels numa tela de 1080 linhas (0 desliga). */
  outline?: number;
  /** Nível de detalhe inicial (0, 1 ou 2). */
  lod?: number;
}

/** Distâncias de troca de LOD (m): até 15 m o LOD0, até 35 m o LOD1, além disso o LOD2. */
export const LOD_DISTANCES = [15, 35] as const;
/** Cor do contorno e dos olhos. */
export const INK = 0x1d1a2b;

const byId = <T extends { id: string }>(list: readonly T[], id: string): T => {
  const found = list.find((o) => o.id === id);
  if (!found) throw new Error(`Opção desconhecida: ${id}`);
  return found;
};

/** Rampa de 3 tons do sombreamento cartoon. */
export const ramp = new THREE.DataTexture(new Uint8Array([95, 175, 255]), 3, 1, THREE.RedFormat);
ramp.minFilter = ramp.magFilter = THREE.NearestFilter;
ramp.needsUpdate = true;

export interface AthleteColors {
  skin: number;
  hair: number;
  kit: Kit;
  /** Cor fixa do acessório (canal B da máscara): viseira, madeira do taco ou preto. */
  accessoryFixed: number;
}

/**
 * Material único do atleta. O shader escolhe a cor por parte (`aPart`, ver `PART`): pele e uniforme pelos
 * campos R (camisa), G (calção) e B (botas), com friso na cor secundária; cabelo; olhos; óculos e acessório
 * por máscara RGB. Todos os atletas compartilham o mesmo programa; só os uniforms mudam.
 */
export function athleteMaterial(c: AthleteColors): THREE.MeshToonMaterial {
  const m = new THREE.MeshToonMaterial({ gradientMap: ramp, vertexColors: true, side: THREE.DoubleSide });
  // Dupla face só por causa do cabelo e da capa; na sombra, como o corpo era antes (sem acne de sombra).
  m.shadowSide = THREE.BackSide;
  const color = (hex: number) => ({ value: new THREE.Color(hex) });
  const uniforms = {
    uSkin: color(c.skin),
    uPrimary: color(c.kit.primary),
    uSecondary: color(c.kit.secondary),
    uShorts: color(c.kit.shorts),
    uBoots: color(c.kit.boots),
    uHair: color(c.hair),
    uAccFixed: color(c.accessoryFixed),
    uInk: color(INK),
    uLens: color(ACCESSORY_COLORS.goggleLens),
    uStrap: color(ACCESSORY_COLORS.goggleStrap),
  };
  m.userData["uniforms"] = uniforms;
  m.onBeforeCompile = (s) => {
    Object.assign(s.uniforms, uniforms);
    s.vertexShader =
      "attribute float aPart;\nvarying float vPart;\n" +
      s.vertexShader.replace("#include <begin_vertex>", "#include <begin_vertex>\n vPart = aPart;");
    s.fragmentShader =
      "uniform vec3 uSkin, uPrimary, uSecondary, uShorts, uBoots, uHair, uAccFixed, uInk, uLens, uStrap;\nvarying float vPart;\n" +
      s.fragmentShader.replace(
        "#include <color_fragment>",
        `
        vec3 c;
        if (vPart < 0.5) {
          float shirt = smoothstep(0.48, 0.52, vColor.r);
          float shorts = smoothstep(0.48, 0.52, vColor.g);
          float boots = smoothstep(0.48, 0.52, vColor.b);
          c = mix(uSkin, uPrimary, shirt);
          c = mix(c, uSecondary, shirt * (1.0 - smoothstep(0.63, 0.67, vColor.r)));
          c = mix(c, uShorts, shorts);
          c = mix(c, uSecondary, shorts * (1.0 - smoothstep(0.61, 0.65, vColor.g)));
          c = mix(c, uBoots, boots);
          c = mix(c, uSecondary, boots * (1.0 - smoothstep(0.58, 0.62, vColor.b)));
        } else if (vPart < 1.5) {
          c = uHair;
        } else if (vPart < 2.5) {
          c = uInk;
        } else if (vPart < 3.5) {
          c = uLens * vColor.r + uStrap * vColor.b;
        } else {
          c = uPrimary * vColor.r + uSecondary * vColor.g + uAccFixed * vColor.b;
        }
        diffuseColor.rgb = c;`,
      );
  };
  m.customProgramCacheKey = () => "atleta";
  return m;
}

/**
 * Contorno por casco invertido, com espessura constante na tela: `px` é a largura em pixels numa tela de
 * 1080 linhas (escala com a altura da janela). Olhos, cabelo, óculos e capa não ganham contorno.
 */
export function outlineMaterial(px: number): THREE.MeshBasicMaterial {
  const m = new THREE.MeshBasicMaterial({ color: INK, side: THREE.BackSide });
  const uniforms = { uWidth: { value: (px * 2) / 1080 } };
  m.userData["uniforms"] = uniforms;
  m.onBeforeCompile = (s) => {
    Object.assign(s.uniforms, uniforms);
    s.vertexShader =
      "attribute float aPart;\nuniform float uWidth;\n" +
      s.vertexShader.replace(
        "#include <project_vertex>",
        `#include <project_vertex>
        float aspect = projectionMatrix[1][1] / projectionMatrix[0][0];
        // transformedNormal vem invertido em materiais BackSide (FLIP_SIDED); aqui vale a normal original.
        vec2 dir = (projectionMatrix * vec4(normalMatrix * objectNormal, 0.0)).xy;
        dir.x *= aspect;
        dir = normalize(dir + vec2(1e-6));
        dir.x /= aspect;
        gl_Position.xy += dir * uWidth * gl_Position.w;
        if ((aPart > 0.5 && aPart < 3.5) || aPart > 4.5) gl_Position = vec4(0.0);`,
      );
  };
  m.customProgramCacheKey = () => "contorno-atleta";
  return m;
}

/** Carrega os dois modelos de atleta (GLB com meshopt). `baseUrl` é a pasta que contém atleta_*.glb. */
export async function loadModels(baseUrl = "/assets/athletes/"): Promise<AthleteModels> {
  const loader = new GLTFLoader().setMeshoptDecoder(MeshoptDecoder);
  const [homem, mulher] = await Promise.all([
    loader.loadAsync(`${baseUrl}atleta_homem.glb`),
    loader.loadAsync(`${baseUrl}atleta_mulher.glb`),
  ]);
  return { homem, mulher };
}

function setMorphs(mesh: THREE.Mesh, weights: Readonly<Record<string, number>>) {
  const dict = mesh.morphTargetDictionary;
  const influences = mesh.morphTargetInfluences;
  if (!dict || !influences) return;
  influences.fill(0);
  for (const [name, w] of Object.entries(weights)) {
    const i = dict[name];
    if (i !== undefined) influences[i] = w;
  }
}

/** Pesos de morph do corpo e do rosto para a ficha. */
export function morphWeights(spec: Pick<AthleteSpec, "biotipo" | "faceShape">): Record<string, number> {
  const morphs: Record<string, number> = { ...byId(FACE_SHAPES, spec.faceShape).morphs };
  if (spec.biotipo !== "medio") morphs[spec.biotipo] = 1;
  return morphs;
}

const BODY_LODS = ["Atleta", "Atleta_LOD1", "Atleta_LOD2"] as const;

/** Peças visíveis para a ficha, por nível de detalhe. No LOD2 olhos, sobrancelhas e óculos somem. */
function piecesFor(spec: AthleteSpec, lod: number): { name: string; part: Part }[] {
  const accessory = spec.posicao ? POSITION_ACCESSORY[spec.posicao] : null;
  const helmet = accessory === "Capacete";
  const hair = byId(HAIR_STYLES, spec.hairStyle).mesh;
  const list: { name: string; part: Part }[] = [];
  if (hair && !(helmet && HELMET_HIDES_HAIR)) list.push({ name: hair, part: PART.hair });
  if (spec.beard) list.push({ name: "Barba", part: PART.hair });
  if (lod < 2) {
    list.push({ name: "Eyes", part: PART.ink }, { name: "Eyebrows", part: PART.hair });
    if (spec.goggles && !helmet) list.push({ name: "Oculos", part: PART.goggles });
  }
  if (accessory) list.push({ name: accessory, part: accessory === "Capa" ? PART.cape : PART.accessory });
  return list;
}

interface ModelCache {
  skeleton: ModelSkeleton;
  geometries: Map<string, THREE.BufferGeometry>;
}
const caches = new WeakMap<GLTF, ModelCache>();

function cacheFor(gltf: GLTF): ModelCache {
  let c = caches.get(gltf);
  if (!c) {
    c = { skeleton: modelSkeleton(gltf.scene), geometries: new Map() };
    caches.set(gltf, c);
  }
  return c;
}

function sourceMesh(gltf: GLTF, name: string): THREE.SkinnedMesh {
  const m = gltf.scene.getObjectByName(name);
  if (!(m instanceof THREE.SkinnedMesh)) throw new Error(`Malha ${name} não encontrada no modelo`);
  return m;
}

/** Geometria fundida do LOD pedido; atletas com as mesmas peças dividem a mesma geometria. */
function mergedGeometry(gltf: GLTF, spec: AthleteSpec, lod: number): THREE.BufferGeometry {
  const cache = cacheFor(gltf);
  const pieces = piecesFor(spec, lod);
  const key = `${lod}|${pieces.map((p) => `${p.name}:${p.part}`).join(",")}`;
  let geo = cache.geometries.get(key);
  if (!geo) {
    const body = sourceMesh(gltf, BODY_LODS[lod] ?? "Atleta");
    const merged = mergeAthlete(
      body,
      pieces.map((p) => ({ mesh: sourceMesh(gltf, p.name), part: p.part })),
      cache.skeleton,
    );
    merged.geometry.morphAttributes["position"]?.forEach((a, i) => (a.name = merged.morphs[i] ?? ""));
    geo = merged.geometry;
    cache.geometries.set(key, geo);
  }
  return geo;
}

/** Monta o atleta: uma SkinnedMesh com um material, mais o contorno. Descarte com `disposeAthlete`. */
export function buildAthlete(
  models: AthleteModels,
  spec: AthleteSpec,
  kit: Kit,
  { outline = 3.2, lod = 0 }: BuildOptions = {},
): AthleteRig {
  const gltf = models[spec.sexo];
  const cache = cacheFor(gltf);
  const root = gltf.scene.clone(true);
  // Só a armadura interessa do clone; as malhas originais saem e entra a malha fundida.
  const old: THREE.Object3D[] = [];
  root.traverse((o) => {
    if (o instanceof THREE.Mesh) old.push(o);
  });
  for (const o of old) o.removeFromParent();
  const bones: Record<string, THREE.Bone> = {};
  root.traverse((o) => {
    if (o instanceof THREE.Bone) bones[o.name] = o;
  });
  const src = sourceMesh(gltf, "Atleta");
  const parent = (src.parent && root.getObjectByName(src.parent.name)) ?? root;
  const skeleton = new THREE.Skeleton(
    cache.skeleton.bones.map((name) => {
      const b = bones[name];
      if (!b) throw new Error(`Osso ${name} não encontrado`);
      return b;
    }),
    cache.skeleton.boneInverses,
  );
  const accessory = spec.posicao ? POSITION_ACCESSORY[spec.posicao] : null;
  const material = athleteMaterial({
    skin: byId(SKIN_TONES, spec.skinTone).hex,
    hair: byId(HAIR_COLORS, spec.hairColor).hex,
    kit,
    accessoryFixed:
      accessory === "Capacete"
        ? ACCESSORY_COLORS.visor
        : accessory === "Taco"
          ? ACCESSORY_COLORS.wood
          : 0x000000,
  });
  const geometryAt = (level: number) => mergedGeometry(gltf, spec, level);
  const mesh = new THREE.SkinnedMesh(geometryAt(lod), material);
  mesh.name = "Atleta";
  mesh.castShadow = true;
  mesh.position.copy(src.position);
  mesh.quaternion.copy(src.quaternion);
  mesh.scale.copy(src.scale);
  parent.add(mesh);
  mesh.bind(skeleton, src.bindMatrix.clone());
  mesh.updateMorphTargets();
  setMorphs(mesh, morphWeights(spec));
  let outlineMesh: THREE.SkinnedMesh | null = null;
  if (outline > 0) {
    outlineMesh = new THREE.SkinnedMesh(mesh.geometry, outlineMaterial(outline));
    outlineMesh.name = "Atleta_contorno";
    outlineMesh.position.copy(mesh.position);
    outlineMesh.quaternion.copy(mesh.quaternion);
    outlineMesh.scale.copy(mesh.scale);
    parent.add(outlineMesh);
    outlineMesh.bind(skeleton, mesh.bindMatrix);
    outlineMesh.morphTargetDictionary = mesh.morphTargetDictionary ?? {};
    outlineMesh.morphTargetInfluences = mesh.morphTargetInfluences ?? [];
  }
  const base = typeof src.userData["altura_base_m"] === "number" ? src.userData["altura_base_m"] : 1.8;
  root.scale.setScalar((HEIGHTS[spec.altura].meters ?? 1.75) / base);
  root.updateMatrixWorld(true);
  // A esfera de recorte vem do LOD0 em T e vale para todos os níveis (troca de LOD não recalcula).
  mesh.computeBoundingSphere();
  if (outlineMesh) outlineMesh.boundingSphere = mesh.boundingSphere;
  return {
    root,
    bones,
    mesh,
    outline: outlineMesh,
    lod,
    setGeometryLod: (level: number) => {
      const g = geometryAt(level);
      mesh.geometry = g;
      if (outlineMesh) outlineMesh.geometry = g;
    },
  };
}

/** Libera os materiais do atleta (as geometrias ficam em cache, divididas entre atletas). */
export function disposeAthlete(rig: AthleteRig): void {
  rig.root.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    for (const mat of [o.material].flat() as THREE.Material[]) mat.dispose();
  });
  rig.root.removeFromParent();
}

/** Troca o nível de detalhe do atleta (0, 1 ou 2). */
export function setLod(rig: AthleteRig, level: number): void {
  if (rig.lod === level) return;
  rig.lod = level;
  rig.setGeometryLod(level);
}

export function lodForDistance(d: number): number {
  return d < LOD_DISTANCES[0] ? 0 : d < LOD_DISTANCES[1] ? 1 : 2;
}

function rotateWorld(bone: THREE.Object3D, axis: THREE.Vector3, angle: number) {
  const q = bone.getWorldQuaternion(new THREE.Quaternion());
  const target = new THREE.Quaternion().setFromAxisAngle(axis, angle).multiply(q);
  const parent = (bone.parent ?? bone).getWorldQuaternion(new THREE.Quaternion()).invert();
  bone.quaternion.copy(parent.multiply(target));
  bone.updateMatrixWorld(true);
}

/** Pose de leitura: braços para baixo (o modelo vem em T). */
export function poseStanding(bones: AthleteRig["bones"]): void {
  if (bones["upperarm_l"]) rotateWorld(bones["upperarm_l"], new THREE.Vector3(0, 0, 1), -1.2);
  if (bones["upperarm_r"]) rotateWorld(bones["upperarm_r"], new THREE.Vector3(0, 0, 1), 1.2);
}

export function addLights(
  scene: THREE.Scene,
  { width = 40, shadowSize = 4096 } = {},
): THREE.DirectionalLight {
  scene.add(new THREE.HemisphereLight(0xdfefff, 0x6a7a4a, 1.0));
  const sun = new THREE.DirectionalLight(0xfff0d8, 1.7);
  sun.position.set(6, 10, 8);
  sun.castShadow = true;
  sun.shadow.mapSize.set(shadowSize, shadowSize);
  Object.assign(sun.shadow.camera, { left: -width, right: width, top: 10, bottom: -10 });
  sun.shadow.bias = -0.0004;
  scene.add(sun);
  return sun;
}

// ---------------------------------------------------------------- poses (item 2.5)

const DEG = Math.PI / 180;

export interface RestPose {
  char: THREE.Quaternion;
  local: THREE.Quaternion;
}
export type RestPoses = Map<THREE.Object3D, RestPose>;

/** Guarda a orientação de repouso de cada osso, no espaço do personagem (relativa à raiz). */
export function captureRest(root: THREE.Object3D): RestPoses {
  root.updateMatrixWorld(true);
  const rootInv = root.getWorldQuaternion(new THREE.Quaternion()).invert();
  const rest: RestPoses = new Map();
  root.traverse((o) => {
    if (o === root) return;
    rest.set(o, {
      char: rootInv.clone().multiply(o.getWorldQuaternion(new THREE.Quaternion())),
      local: o.quaternion.clone(),
    });
  });
  return rest;
}

/**
 * Aplica uma pose (graus, eixos do personagem, relativa à pose em T) com a semântica de packages/anim:
 * pose_do_osso = (rotação acumulada do pai) · D · repouso_do_osso.
 */
export function applyPose(root: THREE.Object3D, rest: RestPoses, pose: Pose): void {
  const posed = new Map<THREE.Object3D, THREE.Quaternion>();
  const euler = new THREE.Euler();
  const d = new THREE.Quaternion();
  root.traverse((o) => {
    if (!(o instanceof THREE.Bone)) return;
    const r = rest.get(o);
    if (!r) return;
    const parent = o.parent;
    const parentRest = (parent && rest.get(parent)?.char) ?? new THREE.Quaternion();
    const parentPosed = (parent && posed.get(parent)) ?? parentRest;
    const g = parentPosed.clone().multiply(parentRest.clone().invert());
    const e = pose[o.name as AnimBone];
    if (e) d.setFromEuler(euler.set(e[0] * DEG, e[1] * DEG, e[2] * DEG, "XYZ"));
    else d.identity();
    const charPosed = g.multiply(d).multiply(r.char);
    posed.set(o, charPosed);
    o.quaternion.copy(parentPosed.clone().invert().multiply(charPosed));
  });
  root.updateMatrixWorld(true);
}

/** Volta todos os ossos à pose de repouso (em T). */
export function resetPose(root: THREE.Object3D, rest: RestPoses): void {
  for (const [o, r] of rest) o.quaternion.copy(r.local);
  root.updateMatrixWorld(true);
}

export interface Broom extends THREE.Group {
  userData: {
    /** Centro de cada apoio de pé [direito, esquerdo], no espaço da vassoura (ajustado por mountOnBroom). */
    footrests: THREE.Vector3[];
    /** Pontos do cabo onde as mãos seguram (a esquerda mais à frente). */
    grips: { l: THREE.Vector3; r: THREE.Vector3 };
    metal: number;
  };
}

const BROOM_COLORS = { wood: 0x8a5a2b, straw: 0xe0b65c, metal: 0xc9a03a } as const;
const broomMaterial = new THREE.MeshToonMaterial({ gradientMap: ramp, vertexColors: true });
const broomOutline = new THREE.MeshBasicMaterial({ color: INK, side: THREE.BackSide });

/** Peça colorida e transformada, e o casco do contorno (1,06× em torno da origem da peça). */
function broomPart(geo: THREE.BufferGeometry, color: number, matrix: THREE.Matrix4) {
  const part = geo.index ? geo.toNonIndexed() : geo.clone();
  part.deleteAttribute("uv");
  const n = part.getAttribute("position").count;
  const c = new THREE.Color(color);
  const colors = new Float32Array(n * 3);
  for (let i = 0; i < n; i++) c.toArray(colors, i * 3);
  part.setAttribute("color", new THREE.BufferAttribute(colors, 3));
  const hull = new THREE.BufferGeometry().setAttribute("position", part.getAttribute("position").clone());
  hull.scale(1.06, 1.06, 1.06);
  part.applyMatrix4(matrix);
  hull.applyMatrix4(matrix);
  geo.dispose();
  return { part, hull };
}

function concat(geos: THREE.BufferGeometry[]): THREE.BufferGeometry {
  const out = new THREE.BufferGeometry();
  for (const name of Object.keys(geos[0]?.attributes ?? {})) {
    const size = geos[0]?.getAttribute(name).itemSize ?? 3;
    const total = geos.reduce((n, g) => n + g.getAttribute(name).array.length, 0);
    const arr = new Float32Array(total);
    let o = 0;
    for (const g of geos) {
      arr.set(g.getAttribute(name).array as Float32Array, o);
      o += g.getAttribute(name).array.length;
    }
    out.setAttribute(name, new THREE.BufferAttribute(arr, size));
  }
  for (const g of geos) g.dispose();
  out.computeBoundingSphere();
  return out;
}

/** Refaz a malha da vassoura (1 malha + 1 contorno), com os apoios de pé onde estão agora. */
function rebuildBroom(g: Broom): void {
  for (const child of [...g.children]) {
    if (child instanceof THREE.Mesh) child.geometry.dispose();
    g.remove(child);
  }
  const m = (x: number, y: number, z: number, rx = 0) =>
    new THREE.Matrix4().compose(
      new THREE.Vector3(x, y, z),
      new THREE.Quaternion().setFromEuler(new THREE.Euler(rx, 0, 0)),
      new THREE.Vector3(1, 1, 1),
    );
  const parts = [
    broomPart(
      new THREE.CylinderGeometry(0.022, 0.026, 1.7, 12),
      BROOM_COLORS.wood,
      m(0, 0, 0.25, Math.PI / 2),
    ),
    broomPart(
      new THREE.ConeGeometry(0.15, 0.55, 14, 1, true),
      BROOM_COLORS.straw,
      m(0, 0, -0.78, -Math.PI / 2),
    ),
    broomPart(new THREE.CylinderGeometry(0.05, 0.05, 0.07, 14), g.userData.metal, m(0, 0, -0.5, Math.PI / 2)),
    ...g.userData.footrests.map((p) =>
      broomPart(new THREE.BoxGeometry(0.11, 0.02, 0.08), g.userData.metal, m(p.x, p.y, p.z)),
    ),
  ];
  const mesh = new THREE.Mesh(concat(parts.map((p) => p.part)), broomMaterial);
  mesh.name = "Vassoura";
  mesh.castShadow = true;
  const hull = new THREE.Mesh(concat(parts.map((p) => p.hull)), broomOutline);
  hull.name = "Vassoura_contorno";
  g.add(mesh, hull);
}

/** Vassoura simples de 2 m feita em código (o modelo definitivo, com cosméticos, vem no item 7.5). */
export function buildBroom(kit?: Kit): Broom {
  const g = new THREE.Group() as Broom;
  g.userData = {
    footrests: [new THREE.Vector3(0.1, -0.3, -0.2), new THREE.Vector3(-0.1, -0.3, -0.2)],
    grips: { l: new THREE.Vector3(0.035, 0.03, 0.42), r: new THREE.Vector3(-0.035, 0.03, 0.3) },
    metal: kit?.secondary ?? BROOM_COLORS.metal,
  };
  rebuildBroom(g);
  return g;
}

/** Libera as geometrias da vassoura (os materiais são compartilhados). */
export function disposeBroom(broom: Broom): void {
  broom.traverse((o) => {
    if (o instanceof THREE.Mesh) o.geometry.dispose();
  });
  broom.removeFromParent();
}

/** Coloca o atleta sentado na vassoura: a vassoura passa logo abaixo da pelve e os apoios vão sob os pés. */
export function mountOnBroom(rig: AthleteRig, broom: Broom): THREE.Group {
  const holder = new THREE.Group();
  holder.add(broom);
  holder.add(rig.root);
  rig.root.updateMatrixWorld(true);
  const pelvisBone = rig.bones["pelvis"];
  if (pelvisBone) {
    const pelvis = pelvisBone.getWorldPosition(new THREE.Vector3());
    rig.root.position.sub(new THREE.Vector3(pelvis.x, pelvis.y - 0.13, pelvis.z - 0.12));
  }
  rig.root.updateMatrixWorld(true);
  holder.updateMatrixWorld(true);
  const inv = holder.matrixWorld.clone().invert();
  broom.userData.footrests.forEach((rest, i) => {
    const side = i === 0 ? "r" : "l";
    const ball = rig.bones[`ball_${side}`] ?? rig.bones[`foot_${side}`];
    if (!ball) return;
    const p = ball.getWorldPosition(new THREE.Vector3()).applyMatrix4(inv);
    rest.set(p.x, p.y - 0.035, p.z + 0.02);
  });
  rebuildBroom(broom);
  return holder;
}

/** Gira um osso em espaço de mundo (pré-multiplica a orientação de mundo). */
function rotateBoneWorld(bone: THREE.Object3D, q: THREE.Quaternion) {
  const world = bone.getWorldQuaternion(new THREE.Quaternion());
  const parentInv = (bone.parent ?? bone).getWorldQuaternion(new THREE.Quaternion()).invert();
  bone.quaternion.copy(parentInv.multiply(q.clone().multiply(world)));
  bone.updateMatrixWorld(true);
}

const angleBetween = (u: THREE.Vector3, v: THREE.Vector3) =>
  Math.acos(THREE.MathUtils.clamp(u.clone().normalize().dot(v.clone().normalize()), -1, 1));

/**
 * IK analítica de dois ossos (ombro, cotovelo) para levar o pulso até `target`, com o cotovelo para o lado de `pole`.
 * `weight` mistura o resultado com a pose que já estava aplicada.
 */
export function solveArmIK(
  bones: AthleteRig["bones"],
  side: "l" | "r",
  target: THREE.Vector3,
  pole: THREE.Vector3,
  weight = 1,
): void {
  const upper = bones[`upperarm_${side}`];
  const lower = bones[`lowerarm_${side}`];
  const hand = bones[`hand_${side}`];
  if (weight <= 0 || !upper || !lower || !hand) return;
  const before = [upper.quaternion.clone(), lower.quaternion.clone()] as const;
  const a = upper.getWorldPosition(new THREE.Vector3());
  const b = lower.getWorldPosition(new THREE.Vector3());
  const c = hand.getWorldPosition(new THREE.Vector3());
  const lab = b.distanceTo(a);
  const lcb = c.distanceTo(b);
  const lat = THREE.MathUtils.clamp(target.distanceTo(a), 0.01, lab + lcb - 0.001);
  const acAb0 = angleBetween(c.clone().sub(a), b.clone().sub(a));
  const baBc0 = angleBetween(a.clone().sub(b), c.clone().sub(b));
  const acAt0 = angleBetween(c.clone().sub(a), target.clone().sub(a));
  const acAb1 = Math.acos(
    THREE.MathUtils.clamp((lcb * lcb - lab * lab - lat * lat) / (-2 * lab * lat), -1, 1),
  );
  const baBc1 = Math.acos(
    THREE.MathUtils.clamp((lat * lat - lab * lab - lcb * lcb) / (-2 * lab * lcb), -1, 1),
  );
  const axis0 = c.clone().sub(a).cross(pole.clone().sub(a)).normalize();
  const axis1 = c.clone().sub(a).cross(target.clone().sub(a)).normalize();
  rotateBoneWorld(upper, new THREE.Quaternion().setFromAxisAngle(axis0, acAb1 - acAb0));
  rotateBoneWorld(lower, new THREE.Quaternion().setFromAxisAngle(axis0, baBc1 - baBc0));
  if (axis1.lengthSq() > 1e-8) rotateBoneWorld(upper, new THREE.Quaternion().setFromAxisAngle(axis1, acAt0));
  if (weight < 1) {
    upper.quaternion.copy(before[0].clone().slerp(upper.quaternion.clone(), weight));
    lower.quaternion.copy(before[1].clone().slerp(lower.quaternion.clone(), weight));
    upper.updateMatrixWorld(true);
  }
}

/** Leva as mãos aos pontos de pegada do cabo. `grip` = { l, r } com pesos de 0 a 1. */
export function gripBroom(rig: AthleteRig, broom: Broom, grip: Grip = { l: 1, r: 1 }): void {
  broom.updateMatrixWorld(true);
  for (const side of ["l", "r"] as const) {
    const shoulderBone = rig.bones[`upperarm_${side}`];
    if (!shoulderBone) continue;
    const target = broom.localToWorld(broom.userData.grips[side].clone());
    const shoulder = shoulderBone.getWorldPosition(new THREE.Vector3());
    // Cotovelos para fora e um pouco para baixo.
    const out = new THREE.Vector3(side === "l" ? 1 : -1, -0.6, 0).applyQuaternion(
      rig.root.getWorldQuaternion(new THREE.Quaternion()),
    );
    solveArmIK(rig.bones, side, target, shoulder.clone().add(out), grip[side]);
  }
}
